import clsx from 'clsx'
import map from 'lodash/map'
import React, { FC } from 'react'

import { ImageV3 } from '@Components/Image'
import { Link } from '@Components/Link'
import { Image } from '@Globals/Image'

import styles from './ImageGrid.module.scss'

interface ImageGridProps {
  id?: string
  images: ImageData[]
  round?: boolean
}

export interface ImageData {
  caption?: string
  destPageType?: string
  destPageTypeId?: string
  image: Image
  link?: string
  linkNewTab?: boolean
}

interface ImageProps {
  caption: string
  image: Image
  round: boolean
}

type ImageLinkProps = Omit<ImageData, 'image'> & {
  children?: React.ReactNode
}

const ImageLink: FC<ImageLinkProps> = ({
  children,
  destPageType,
  destPageTypeId,
  link,
  linkNewTab = false,
}) => (
  <Link
    analytics={{
      dest_page_type: destPageType || '',
      dest_page_type_id: destPageTypeId || '',
      source_id: 'image_grid_module',
    }}
    className={styles.imageItem}
    href={link}
    target={linkNewTab ? '_blank' : undefined}
  >
    {children}
  </Link>
)

const WrappedImage: FC<ImageProps> = ({ caption, image, round }) => (
  <>
    <div className={clsx(styles.imageWrapper, round && styles.round)}>
      {image.url && (
        <ImageV3
          alt={caption || ''}
          height={image.dimensions?.height}
          loading="lazy"
          sizes="165px"
          src={image.url}
          width={image.dimensions?.width}
        />
      )}
    </div>
    <p className={styles.imageCaption}>{caption}</p>
  </>
)

export const ImageGrid: FC<ImageGridProps> = ({ id, images, round = false }) => (
  <div className={styles.imageGrid} id={id}>
    {map(images, ({
      caption,
      image,
      link,
      ...props
    }, index) => {
      if (link) {
        return (
          <ImageLink key={`${index}-${link}`} {...props} link={link}>
            <WrappedImage caption={caption || ''} image={image} round={round} />
          </ImageLink>
        )
      }
      return (
        <div key={`${index}-${caption}`} className={styles.imageItem}>
          <WrappedImage caption={caption || ''} image={image} round={round} />
        </div>
      )
    })}
  </div>
)
